import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Settings2, Trophy, Radio, MessageSquare, Users, Swords, Timer, BarChart3, Flag, Zap, Dot } from "lucide-react";
import { toast } from "sonner";
import { TurfTalk } from "@/api/client";
import { PageHeader, Empty, Skeleton } from "@/components/ui-kit";
import {
  normalizeFeed, normalizePreferences, buildPreferenceUpdate, categoryLabel,
} from "@/lib/notifications";

const CATEGORY_ICON = {
  team_kickoff: Flag, game_live: Radio, dm_reply: MessageSquare, room_invite: Users,
  rivalry_invite: Swords, pick_deadline: Timer, prediction_graded: BarChart3,
  matchup_result: Trophy, award: Trophy, close_game: Zap,
};

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function NotificationRow({ n, onOpen }) {
  const Icon = CATEGORY_ICON[n.category] || Bell;
  return (
    <button
      onClick={() => onOpen(n)}
      data-testid={`notification-${n.id}`}
      data-unread={!n.read}
      className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-[#2A3544] transition-colors hover:bg-white/5 ${n.read ? "" : "bg-[#16211a]"}`}
    >
      <div className={`mt-0.5 w-9 h-9 shrink-0 grid place-items-center rounded-lg ${n.read ? "bg-white/5 text-white/50" : "bg-[#24362A] text-[#4ea152]"}`}>
        <Icon size={16} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide text-white/40">{n.label}</span>
          {!n.read && <Dot className="text-[#4ea152] -mx-1" size={18} data-testid={`notification-unread-${n.id}`} />}
          <span className="ml-auto text-[11px] text-white/35 shrink-0">{timeAgo(n.createdAt)}</span>
        </div>
        <div className={`text-sm mt-0.5 ${n.read ? "text-white/70" : "text-white"}`}>{n.title}</div>
        {n.body && <div className="text-[13px] text-white/50 mt-0.5 whitespace-pre-wrap leading-relaxed">{n.body}</div>}
      </div>
    </button>
  );
}

function PreferencesPanel({ prefs, onToggle, saving }) {
  if (!prefs) return <Skeleton className="h-48" />;
  return (
    <div className="tt-panel rounded-xl p-4" data-testid="notification-preferences">
      <div className="flex items-center gap-2 mb-3">
        <Settings2 size={15} className="text-white/50" />
        <span className="font-display tracking-widest text-xs text-white/40">PREFERENCES</span>
      </div>
      {prefs.items.length === 0
        ? <p className="text-sm text-white/50">No preference categories reported.</p>
        : (
          <div className="space-y-1">
            {prefs.items.map((it) => (
              <label key={it.key} className="flex items-start gap-3 py-2 cursor-pointer" data-testid={`pref-row-${it.key}`}>
                <input
                  type="checkbox"
                  checked={it.enabled}
                  disabled={saving === it.key}
                  onChange={(e) => onToggle(it.key, e.target.checked)}
                  className="mt-0.5 accent-[#2F7D32] w-4 h-4"
                  data-testid={`pref-toggle-${it.key}`}
                />
                <span className="min-w-0">
                  <span className="text-sm text-white block">{it.label}</span>
                  {it.description && <span className="text-[12px] text-white/45 block">{it.description}</span>}
                </span>
              </label>
            ))}
          </div>
        )}
      {(prefs.mutedTeams.length > 0 || prefs.mutedRooms.length > 0) && (
        <div className="mt-3 pt-3 border-t border-[#2A3544] text-[12px] text-white/45" data-testid="pref-muted-summary">
          Muted: {prefs.mutedTeams.length} team{prefs.mutedTeams.length === 1 ? "" : "s"}, {prefs.mutedRooms.length} room{prefs.mutedRooms.length === 1 ? "" : "s"}.
        </div>
      )}
    </div>
  );
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [feed, setFeed] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [savingPref, setSavingPref] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = normalizeFeed(await TurfTalk.notifications({ limit: 20 }));
      setFeed(r.items);
      setCursor(r.nextCursor);
      setHasMore(r.hasMore);
    } catch {
      setFeed([]);
      setCursor(null);
      setHasMore(false);
    }
  }, []);

  const loadPrefs = useCallback(async () => {
    try { setPrefs(normalizePreferences(await TurfTalk.notificationPreferences())); }
    catch { setPrefs(normalizePreferences({})); }
  }, []);

  useEffect(() => { load(); loadPrefs(); }, [load, loadPrefs]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = normalizeFeed(await TurfTalk.notifications({ limit: 20, cursor }));
      setFeed((prev) => [...(prev || []), ...r.items]);
      setCursor(r.nextCursor);
      setHasMore(r.hasMore);
    } catch { toast.error("Couldn't load more notifications."); }
    finally { setLoadingMore(false); }
  };

  const open = async (n) => {
    if (!n.read && n.id) {
      setFeed((prev) => (prev || []).map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      TurfTalk.notificationRead(n.id).catch(() => {});
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    setMarkingAll(true);
    try {
      await TurfTalk.notificationsReadAll();
      setFeed((prev) => (prev || []).map((x) => ({ ...x, read: true })));
      toast.success("All notifications marked as read");
    } catch { toast.error("Couldn't mark all as read."); }
    finally { setMarkingAll(false); }
  };

  const togglePref = async (key, value) => {
    const prev = prefs;
    setSavingPref(key);
    setPrefs((p) => ({ ...p, items: p.items.map((it) => (it.key === key ? { ...it, enabled: value } : it)), categories: { ...p.categories, [key]: value } }));
    try {
      setPrefs(normalizePreferences(await TurfTalk.updateNotificationPreferences(buildPreferenceUpdate(prev, key, value))));
    } catch {
      setPrefs(prev);
      toast.error("Couldn't update preference.");
    } finally { setSavingPref(null); }
  };

  const hasUnread = (feed || []).some((n) => !n.read);

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8" data-testid="notifications-page">
      <PageHeader
        title="Notifications"
        subtitle="Alerts about your teams, games, rooms and predictions."
        right={
          <button
            onClick={markAll}
            disabled={!hasUnread || markingAll}
            data-testid="mark-all-read"
            className="flex items-center gap-2 h-10 px-4 rounded-lg bg-[#24362A] text-white text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#2b4232] transition-colors"
          >
            <CheckCheck size={15} /> Mark all read
          </button>
        }
      />
      <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
        <div className="tt-panel rounded-xl overflow-hidden">
          {feed === null
            ? <Skeleton className="h-64 m-4" />
            : feed.length === 0
              ? <div className="p-4"><Empty title="You're all caught up" detail="New alerts about your teams, games and rooms will show up here." icon={Bell} /></div>
              : (
                <>
                  <div data-testid="notification-list">
                    {feed.map((n) => <NotificationRow key={n.id} n={n} onOpen={open} />)}
                  </div>
                  {hasMore && (
                    <div className="p-3">
                      <button onClick={loadMore} disabled={loadingMore} data-testid="notifications-load-more" className="w-full h-10 rounded-lg tt-panel-3 text-sm text-white/70 hover:text-white disabled:opacity-50">
                        {loadingMore ? "Loading…" : "Load more"}
                      </button>
                    </div>
                  )}
                </>
              )}
        </div>
        <PreferencesPanel prefs={prefs} onToggle={togglePref} saving={savingPref} />
      </div>
    </div>
  );
}

export { categoryLabel };
